import Navigation from '../../components/feature/Navigation';
import SEOHead from '../../components/feature/SEOHead';
import PublicationCard from './components/PublicationCard';
import { publications } from '../../mocks/publications';
import { useScrollReveal, revealClass } from '../../hooks/useScrollReveal';

const publicationsSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  '@id': 'https://oludamolaadebowale.com/publications#collectionpage',
  url: 'https://oludamolaadebowale.com/publications',
  name: 'Publications, Oludamola Adebowale',
  description:
    'Complete catalogue of books, exhibition catalogues, scholarly articles, and gazettes authored and curated by Nigerian cultural historian and archivist Oludamola Adebowale.',
  mainEntity: { '@id': 'https://oludamolaadebowale.com/#person' },
  author: { '@id': 'https://oludamolaadebowale.com/#person' },
  inLanguage: 'en',
  dateModified: '2026-04-05',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://oludamolaadebowale.com/' },
      { '@type': 'ListItem', position: 2, name: 'Publications', item: 'https://oludamolaadebowale.com/publications' },
    ],
  },
};

// Custom order as specified by IDs
const orderedIds = [1, 2, 3, 4, 5, 10, 7];
const sorted = [
  ...orderedIds.map((id) => publications.find((p) => p.id === id)).filter(Boolean),
  ...publications.filter((p) => !orderedIds.includes(p.id)),
] as typeof publications;

export default function PublicationsPage() {
  const { ref: headingRef, isVisible: headingVisible } = useScrollReveal<HTMLDivElement>();
  const { ref: gridRef, isVisible: gridVisible } = useScrollReveal<HTMLDivElement>({ threshold: 0.05 });

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Publications | Oludamola Adebowale, Books, Catalogues and Scholarly Articles"
        description="Explore all books, exhibition catalogues, scholarly articles, and gazettes by Oludamola Adebowale, including Fabric of a Nation, History of Lagos, and In the Beginning There Was Cloth."
        keywords="Oludamola Adebowale books, Fabric of a Nation, History of Lagos, Nigerian cultural publications, West African textile history, Lagos history book, Nigerian archivist publications"
        canonical="https://oludamolaadebowale.com/publications"
        ogType="website"
        schemaJson={publicationsSchema}
      />
      <Navigation />

      {/* Page header — dark themed, consistent with site style */}
      <div className="bg-[#1a1714] pt-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div ref={headingRef} className={`${revealClass(headingVisible)}`}>
            <p className="text-xs uppercase tracking-widest text-[#b89a6a] mb-4 font-sans">
              SCHOLARLY OUTPUT
            </p>
            <h1 className="font-serif text-4xl lg:text-5xl font-bold text-white mb-6">
              Books &amp; Publications
            </h1>
            <p className="font-sans text-base text-gray-400 max-w-2xl">
              This section highlights books and publications I have contributed to as a writer, researcher, author, and contributing editor.
            </p>
          </div>
        </div>
      </div>

      {/* Publications grid */}
      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="mb-12">
          <h2 className="font-serif text-3xl lg:text-4xl font-bold text-gray-900">
            Books and Publications
          </h2>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-start">
          {sorted.map((pub, i) => (
            <PublicationCard
              key={pub.id}
              publication={pub}
              className={revealClass(gridVisible, Math.min(i * 80, 400))}
            />
          ))}
        </div>
      </main>

      <div className="bg-[#f9f6f1] border-t border-stone-200 py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <p className="font-sans text-sm text-gray-500">
            For academic inquiries, permissions, or collaboration requests, please{' '}
            <a href="/press#contact" className="text-gray-900 underline underline-offset-2 hover:text-accent transition-colors cursor-pointer">
              get in touch
            </a>.
          </p>
        </div>
      </div>
    </div>
  );
}
